// src/components/admin/AdminCMPermissions.jsx
import React, { useState, useEffect } from 'react';
import { adminCMPermissionAPI } from '../../services/api';
import { notify, confirmDialog } from '../../utils/notify';

const AdminCMPermissions = () => {
  const [facultyList, setFacultyList] = useState([]);
  const [filteredList, setFilteredList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [actionLoading, setActionLoading] = useState({});

  useEffect(() => {
    fetchFaculty();
  }, []);

  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredList(facultyList);
    } else {
      const q = searchQuery.toLowerCase();
      setFilteredList(
        facultyList.filter(f =>
          (f.first_name || '').toLowerCase().includes(q) ||
          (f.last_name || '').toLowerCase().includes(q) ||
          (f.username || '').toLowerCase().includes(q) ||
          (f.email || '').toLowerCase().includes(q) ||
          (f.profile?.employee_id || '').toLowerCase().includes(q)
        )
      );
    }
  }, [searchQuery, facultyList]);

  const fetchFaculty = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await adminCMPermissionAPI.getCMFaculty();
      setFacultyList(data);
      setFilteredList(data);
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.error || 'Failed to load Community Medicine faculty list.');
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePermission = async (faculty) => {
    const facultyId = faculty.id;
    const hasPermission = faculty.has_permission;
    
    setActionLoading(prev => ({ ...prev, [facultyId]: true }));
    try {
      if (hasPermission) {
        if (!await confirmDialog(`Are you sure you want to revoke Thesis/Ethics approval permission from ${faculty.first_name} ${faculty.last_name}?`)) {
          return;
        }
        await adminCMPermissionAPI.revokeCMPermission(facultyId);
      } else {
        await adminCMPermissionAPI.grantCMPermission(facultyId);
      }
      // Reload list
      const updatedData = await adminCMPermissionAPI.getCMFaculty();
      setFacultyList(updatedData);
    } catch (err) {
      notify.error(err.response?.data?.error || 'Failed to update permission.');
    } finally {
      setActionLoading(prev => ({ ...prev, [facultyId]: false }));
    }
  };

  return (
    <div className="cm-permissions-container" style={{ padding: '8px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h3 style={{ margin: 0, fontSize: '1.2rem', fontWeight: 600, color: '#1e293b' }}>
            Community Medicine Faculty Permissions
          </h3>
          <p style={{ margin: '4px 0 0', fontSize: '0.85rem', color: '#64748b' }}>
            Grant or revoke Thesis and Ethics review authorization to Community Medicine faculty members.
          </p>
        </div>
      </div>

      <div style={{ marginBottom: '16px' }}>
        <input
          type="search"
          placeholder="Search by name, employee ID, username or email..."
          className="search-input"
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          style={{ width: '100%', maxWidth: '450px', padding: '8px 12px', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '0.9rem' }}
        />
      </div>

      {error && (
        <div className="error-alert" style={{ marginBottom: '16px', display: 'flex', alignItems: 'center' }}>
          <span>{error}</span>
        </div>
      )}

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
          <div className="spinner-large" />
          <span style={{ marginLeft: '12px', color: '#64748b' }}>Loading Community Medicine faculty...</span>
        </div>
      ) : filteredList.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px 20px', background: '#f8fafc', borderRadius: '12px', border: '1px dashed #cbd5e1', color: '#64748b' }}>
          {searchQuery ? 'No matching faculty found.' : 'No Community Medicine faculty members registered.'}
        </div>
      ) : (
        <div className="table-container" style={{ border: '1px solid #e2e8f0', borderRadius: '12px', overflow: 'hidden' }}>
          <table className="data-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                <th style={{ textAlign: 'left', padding: '12px 16px' }}>Employee ID</th>
                <th style={{ textAlign: 'left', padding: '12px 16px' }}>Name</th>
                <th style={{ textAlign: 'left', padding: '12px 16px' }}>Email</th>
                <th style={{ textAlign: 'left', padding: '12px 16px' }}>Sub-role</th>
                <th style={{ textAlign: 'center', padding: '12px 16px' }}>Status</th>
                <th style={{ textAlign: 'right', padding: '12px 16px' }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredList.map(faculty => {
                const hasPerm = faculty.has_permission;
                const isBtnLoading = actionLoading[faculty.id];
                return (
                  <tr key={faculty.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '12px 16px' }}>
                      <strong>{faculty.profile?.employee_id || 'N/A'}</strong>
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <strong>{faculty.first_name} {faculty.last_name}</strong>
                    </td>
                    <td style={{ padding: '12px 16px', color: '#475569' }}>
                      {faculty.email}
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <span className="badge badge-blue">
                        {faculty.profile?.faculty_subrole?.replace(/_/g, ' ') || 'FACULTY'}
                      </span>
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                      <span className={`badge ${hasPerm ? 'badge-green' : 'badge-gray'}`}>
                        {hasPerm ? 'Authorized Reviewer' : 'No Authorization'}
                      </span>
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                      <button
                        onClick={() => handleTogglePermission(faculty)}
                        disabled={isBtnLoading}
                        style={{
                          padding: '6px 12px',
                          borderRadius: '6px',
                          fontSize: '0.82rem',
                          fontWeight: '600',
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          border: 'none',
                          backgroundColor: hasPerm ? '#fee2e2' : '#dcfce7',
                          color: hasPerm ? '#b91c1c' : '#166534',
                        }}
                      >
                        {isBtnLoading ? 'Processing...' : hasPerm ? 'Revoke Reviewer' : 'Grant Reviewer'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminCMPermissions;
